import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, Users, ShieldCheck, SprayCan, Medal, UserCheck, Clock, SlidersHorizontal } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { marqueeItems, images } from "../data/site";

const whyReasons = [
  {
    icon: Medal,
    title: "Founded by a Veteran",
    text: "Leadership by an Ex-Serviceman guarantees a hierarchy based on discipline and accountability.",
  },
  {
    icon: UserCheck,
    title: "Rigorous Training",
    text: "Our staff undergoes strict background checks and physical training before deployment.",
  },
  {
    icon: Clock,
    title: "24/7 Support",
    text: "Security never sleeps, and neither do we. We offer round-the-clock supervision and support.",
  },
  {
    icon: SlidersHorizontal,
    title: "Customized Solutions",
    text: "We understand that a hospital has different security needs than a construction site. We tailor our plans to fit your specific risks.",
  },
];

const starValues = [
  {
    letter: "S",
    title: "Safety First",
    text: "Creating a secure environment is our primary goal. We assess risks and neutralize threats to ensure peace of mind for our clients.",
  },
  {
    letter: "T",
    title: "Trustworthiness",
    text: "Founded by an Ex-Serviceman, our reputation is built on reliability. When you hire STARLINK, you hire a partner you can count on 24/7.",
  },
  {
    letter: "A",
    title: "Adaptability",
    text: "From corporate housekeeping to personal bodyguards, we adapt our skills to meet the specific needs of the environment and the client.",
  },
  {
    letter: "R",
    title: "Respect",
    text: "We treat every client, guest, and property with the utmost dignity and respect, ensuring a professional atmosphere at all times.",
  },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 600], [0, 90]);

  return (
    <section className="relative overflow-hidden bg-[#F8FAFC]">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-sky-100/60 blur-3xl" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-7 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#E5322D] shadow-[0_0_8px_2px_rgba(229,50,45,0.55)] animate-pulse" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500">
              Est. 2023 / Precision Facility Infrastructure
            </span>
          </motion.div>

          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-slate-900"
            data-testid="hero-headline"
          >
            <MaskedLine delay={0.1}>Security You Can Trust,</MaskedLine>
            <MaskedLine delay={0.22} className="italic text-[#0F2A4A]">Service Beyond Measure.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600"
            data-testid="hero-intro"
          >
            Established in 2023 under the disciplined leadership of Ex-Serviceman Ashok Kumar,
            STARLINK Security Force is built on the foundations of integrity, valor, and vigilance.
            We are more than just a security agency; we are a comprehensive solution for safety and
            facility management. From providing elite personal bodyguards and event security to
            professional housekeeping services, we bring military-grade precision to every task,
            ensuring your environment is not only secure but also impeccably maintained.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/services/security"
              data-testid="hero-explore-services-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0F2A4A] px-7 py-3.5 text-sm font-semibold text-white hover:bg-sky-700 transition-colors duration-300"
            >
              Explore Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-800 hover:border-sky-600 hover:text-sky-700 transition-colors duration-300"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yImage }} className="overflow-hidden rounded-2xl">
              <img
                src={images.team}
                alt="STARLINK Security team on duty"
                className="h-[26rem] lg:h-[32rem] w-full object-cover object-[50%_20%]"
                data-testid="hero-image"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const whatWeDoCards = [
  {
    icon: Users,
    title: "General Staffing",
    text: 'Our Customised & Innovative "Staffing Solutions" address a complete range of profile requirements.',
    testId: "whatwedo-card-staffing",
  },
  {
    icon: ShieldCheck,
    title: "Security",
    text: "Our Security Services appreciate the importance of providing a secure environment for all our clients.",
    testId: "whatwedo-card-security",
  },
  {
    icon: SprayCan,
    title: "House Keeping",
    text: "Housekeeping is not limited to just cleanliness alone. A thoroughly cleaned, well-maintained space is our promise.",
    testId: "whatwedo-card-housekeeping",
  },
];

const WhatWeDoCard = ({ icon: Icon, title, text, testId }) => (
  <div
    data-testid={testId}
    className="h-full rounded-2xl border border-slate-100 bg-white px-8 py-12 text-center shadow-[0_18px_50px_-20px_rgba(15,23,42,0.15)] hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-20px_rgba(229,50,45,0.2)] transition-all duration-300"
  >
    <Icon className="mx-auto h-14 w-14 text-[#E5322D]" strokeWidth={1.25} />
    <h3 className="mt-6 text-2xl font-extrabold tracking-tight text-slate-900">{title}</h3>
    <p className="mt-4 text-base leading-relaxed text-slate-500">{text}</p>
  </div>
);

const WhatWeDo = () => (
  <section className="bg-white py-20 lg:py-28" data-testid="what-we-do-section">
    <div className="mx-auto max-w-6xl px-6 lg:px-10">
      <Reveal className="flex justify-center">
        <h2
          className="font-sans text-4xl sm:text-5xl font-extrabold tracking-tight text-[#E5322D]"
          data-testid="what-we-do-heading"
        >
          What We Do
        </h2>
      </Reveal>
      <div className="mt-14 mx-auto max-w-md">
        <Reveal delay={0.05}>
          <WhatWeDoCard {...whatWeDoCards[1]} />
        </Reveal>
      </div>
      <div className="mt-8 grid sm:grid-cols-2 gap-8">
        <Reveal delay={0.15} className="h-full">
          <WhatWeDoCard {...whatWeDoCards[0]} />
        </Reveal>
        <Reveal delay={0.25} className="h-full">
          <WhatWeDoCard {...whatWeDoCards[2]} />
        </Reveal>
      </div>
    </div>
  </section>
);

const numberStats = [
  { value: 30, label: "No. of Clients" },
  { value: 45, label: "No. of Locations" },
  { value: 250, label: "No. of Employees" },
  { value: 2, label: "No. of States" },
];

const Counter = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return <span ref={ref}>{display}+</span>;
};

const Manifesto = () => (
  <section className="bg-[#F8FAFC] py-20 lg:py-28" data-testid="numbers-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <h2
          className="text-center font-sans text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900"
          data-testid="numbers-heading"
        >
          Our Numbers in Years
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
        {numberStats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1} className="h-full">
            <div
              data-testid={`number-card-${i}`}
              className="h-full rounded-2xl border border-slate-100 bg-white px-6 py-12 text-center shadow-[0_18px_50px_-20px_rgba(15,23,42,0.12)] hover:-translate-y-1.5 transition-transform duration-300"
            >
              <p className="text-5xl lg:text-6xl font-extrabold tracking-tight text-[#E5322D]">
                <Counter value={s.value} />
              </p>
              <p className="mt-4 font-mono text-xs sm:text-sm tracking-[0.18em] uppercase text-slate-500">
                {s.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-10 text-center text-sm text-slate-400">starlinksecurityforce.com.</p>
    </div>
  </section>
);

const TrustMatrix = () => (
  <section className="bg-white py-20 lg:py-28" data-testid="why-choose-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="text-center max-w-3xl mx-auto">
        <h2
          className="font-sans text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900"
          data-testid="why-choose-heading"
        >
          Why Choose <span className="text-[#E5322D]">STARLINK Security Force</span>?
        </h2>
        <p className="mt-6 text-base leading-relaxed text-slate-600">
          At STARLINK Security Force, we are committed to excellence. When you hire us, you aren't
          just hiring a service provider; you are partnering with a force dedicated to your protection.
        </p>
      </Reveal>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {whyReasons.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.1} className="h-full">
            <div
              data-testid={`why-card-${i}`}
              className="h-full rounded-2xl border border-slate-100 bg-white px-7 py-10 text-center shadow-[0_18px_50px_-20px_rgba(15,23,42,0.12)] hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-20px_rgba(229,50,45,0.18)] transition-all duration-300"
            >
              <r.icon className="mx-auto h-12 w-12 text-[#E5322D]" strokeWidth={1.25} />
              <h3 className="mt-5 text-xl font-extrabold tracking-tight text-slate-900">{r.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">{r.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-20 text-center">
        <h3 className="mt-3 font-sans text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="star-heading">
          THE <span className="text-[#E5322D]">S.T.A.R.</span> STANDARD
        </h3>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {starValues.map((v, i) => (
          <Reveal key={v.letter} delay={i * 0.1} className="h-full">
            <div
              data-testid={`star-value-${v.letter.toLowerCase()}`}
              className="h-full rounded-2xl border border-slate-200/80 bg-[#F8FAFC] p-7 hover:border-[#E5322D]/40 transition-colors duration-300"
            >
              <span className="font-display text-5xl leading-none text-[#E5322D]">{v.letter}</span>
              <h4 className="mt-4 text-lg font-extrabold tracking-tight text-slate-900">{v.title}</h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{v.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={marqueeItems} />
      <WhatWeDo />
      <Manifesto />
      <TrustMatrix />
    </>
  );
}
